import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, switchMap } from 'rxjs/operators';

import { AuthService } from './auth.service';
import { CompanyConfigsService } from './company-configs.service';
import {
  CompanyConfig,
  CompanyDashboardCardKey,
  CompanyModuleKey,
  CompanyServiceKey,
  createDefaultCompanyConfig,
} from '../models';

@Injectable({
  providedIn: 'root',
})
export class AccessControlService {
  private configSubject = new BehaviorSubject<CompanyConfig>(createDefaultCompanyConfig());
  private configLoadedSubject = new BehaviorSubject<boolean>(false);

  config$ = this.configSubject.asObservable();
  configLoaded$ = this.configLoadedSubject.asObservable();

  constructor(
    private authService: AuthService,
    private companyConfigsService: CompanyConfigsService,
  ) {
    this.initializeWatcher();
  }

  /**
   * Observa mudanças de usuário autenticado para carregar as configurações correspondentes.
   */
  private initializeWatcher(): void {
    this.authService.authState$
      .pipe(
        map((state) => state.user?.id || null),
        distinctUntilChanged(),
        switchMap((userId) => {
          const isAdmin = this.authService.isAdmin();

          if (userId && !isAdmin) {
            return this.companyConfigsService.getCurrentCompanyConfig().pipe(
              map((config) => this.mergeWithDefaults(config)),
              catchError(() => of(createDefaultCompanyConfig(userId))),
            );
          }

          if (userId && isAdmin) {
            const defaultConfig = createDefaultCompanyConfig(userId);
            return of(defaultConfig);
          }

          return of(createDefaultCompanyConfig());
        }),
        shareReplay(1),
      )
      .subscribe((config) => {
        this.setConfig(config);
        this.configLoadedSubject.next(true);
      });
  }

  /**
   * Atualiza manualmente as configurações atuais (útil após salvar no admin).
   */
  setConfig(config: CompanyConfig | null): void {
    const mergedConfig = config ? this.mergeWithDefaults(config) : createDefaultCompanyConfig();
    this.configSubject.next(mergedConfig);
  }

  /**
   * Retorna um snapshot imediato das configs locais.
   */
  getConfigSnapshot(): CompanyConfig {
    return this.configSubject.value;
  }

  /**
   * Recarrega as configurações da empresa autenticada.
   */
  refreshCurrentCompanyConfig(): Observable<CompanyConfig> {
    return this.companyConfigsService.getCurrentCompanyConfig().pipe(
      map((config) => this.mergeWithDefaults(config)),
      catchError(() => of(this.getConfigSnapshot())),
      map((config) => {
        this.setConfig(config);
        return config;
      }),
    );
  }

  /**
   * Verifica se um módulo está habilitado.
   */
  isModuleEnabled(moduleKey: CompanyModuleKey): boolean {
    const config = this.getConfigSnapshot();
    return config.modules?.[moduleKey] ?? true;
  }

  /**
   * Verifica se um card do dashboard está habilitado.
   */
  isDashboardCardEnabled(cardKey: CompanyDashboardCardKey): boolean {
    const config = this.getConfigSnapshot();
    return config.dashboard_cards?.[cardKey] ?? true;
  }

  /**
   * Verifica se um serviço específico está habilitado.
   */
  isServiceEnabled(serviceKey: CompanyServiceKey): boolean {
    const config = this.getConfigSnapshot();
    return config.services?.[serviceKey] ?? true;
  }

  private mergeWithDefaults(config: CompanyConfig | null): CompanyConfig {
    const defaults = createDefaultCompanyConfig(config?.company_id ?? 0);

    return {
      ...defaults,
      ...(config || {}),
      modules: {
        ...defaults.modules,
        ...(config?.modules || {}),
      },
      dashboard_cards: {
        ...defaults.dashboard_cards,
        ...(config?.dashboard_cards || {}),
      },
      services: {
        ...defaults.services,
        ...(config?.services || {}),
      },
      mobile: {
        ...defaults.mobile,
        ...(config?.mobile || {}),
      },
      metadata: {
        ...defaults.metadata,
        ...(config?.metadata || {}),
      },
    };
  }
}
